import { FormEventScope } from '../eventing/form-event-scope';
import { FormEvent } from '../eventing/form-event';
import { FormEvents } from './events';

export enum FormOwner {
	Cognito = 'cognito',
	User = 'user'
}
export enum EmbedContext {
	Public = 'public',
	Iframe = 'iframe',
	Seamless = 'seamless',
	Publish = 'publish',
	Preview = 'preview',
	Entries = 'entries',
	SharedTemplatePreview = 'sharedtemplatepreview',
	Overwatch = 'overwatch',
	FormView = 'formview',
	FormLightbox = 'formlightbox',
	GuestRegistration = 'guestregistration',
	GuestRegistrationLightbox = 'guestregistrationlightbox'
}

/**
 * Attempts to extract prefill data from the URL query string and calls `handle.prefill()`
 * when the `FormHandle` is ready if the prefill method has not already been called.
 * @param handle
 */
export async function tryUrlPrefill(handle: FormHandle) {
	// Extract the prefill data from the query string 'entry={"Field1":"value", "Field2":"value"}'
	const prefillMatch = /[?&]entry=([^&#]*)/i.exec(window.location.search);
	if (prefillMatch) {
		try {
			await handle.ready;
		}
		catch (error) {
			// Ignore failure to mount the form (it should be handled/logged elsewhere)
			return;
		}

		// Attempt prefill after the form handle is ready
		try {
			handle.prefill(decodeURIComponent(prefillMatch[1].replace(/\+/g, ' ')));
		}
		catch (e) {
			console.warn(e);
		}
	}
}

/**
 * Provides an interaction layer between the end user and a mounted form.
 */
export class FormHandle extends FormEventScope {
	readonly formId: string;
	readonly owner: FormOwner;

	constructor(formId: string, owner: FormOwner, ready: Promise<any>) {
		super(ready);
		this.formId = formId;
		this.owner = owner;
	}

	/**
	 * Initializes form fields based on the provided entry data.
	 * @param data The entry data used to prefill the form. Can either be an object or a JSON string.
	 */
	prefill(data: object | string): this {
		if (typeof data === 'object')
			data = JSON.stringify(data);

		// only the first call to prefill will do anything
		if (!this.eventLog.includes(FormEvents.Prefill))
			this.emit(new FormEvent(FormEvents.Prefill, { json: data }));

		return this;
	}

	/**
	 * Allows the user to override localized resource strings.
	 * @param resourceName Name of resource to override.
	 * @param value
	 */
	overrideText(resourceName: string, value: string): this {
		this.emit(new FormEvent(FormEvents.OverrideText, { resourceName, value }));
		return this;
	}

	destroy() {
		this.emit(new FormEvent('destroyed' as any), true);
		this.handlers.clear();
	}
}